import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { colourClasses } from "@/lib/colours";
import { SHIFT_STATUS_META, SHIFT_STATUS_ORDER, statusMeta } from "@/lib/shiftStatus";
import { SlidePanel, DayTimeline, ResourceChip, RouteMapMini } from "./panelChrome";
import FormFiller from "@/components/FormFiller";
import {
  Clock, MapPin, Cloud, Thermometer, CheckCircle2, Circle, Loader2,
  Menu, Pencil, Trash2, ListChecks, Plus, ExternalLink, Printer, Navigation,
  Check, Truck, Star, Users,
} from "lucide-react";

const fmtDur = (m) => {
  if (m == null) return "—";
  const h = Math.floor(m / 60), mm = Math.round(m % 60);
  return h ? `${h}h ${mm}m` : `${mm} min`;
};

const ordinal = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };
const _norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const reqMatch = (fn, label) => {
  const a = _norm(fn), b = _norm(label);
  if (!a || !b) return false;
  if (a === b || a.includes(b) || b.includes(a)) return true;
  if (b.includes("vehiclecheck") && a.includes("vehicle") && (a.includes("inspection") || a.includes("check"))) return true;
  return false;
};
// Requirement-label -> form-name aliases (normalised). Edit freely to tune which form
// opens for each on-site requirement. Keys are matched as substrings of the requirement
// label; values are matched as substrings of the form name.
// interpretation-based aliases, HELD pending sign-off (UAT option B — 2026-10-03)
const ALIAS_MATCHING_ENABLED = false;
const REQ_FORM_ALIASES = {
  presiteriskassessment: ["safetowork"],
  riskassessment: ["safetowork", "riskassessment"],
  sitesurvey: ["customertaskformspalocation", "customerformmaintenancesitevisit", "customertaskform"],
  customersignoff: ["installationsignoff"],
  signoff: ["installationsignoff"],
  installeditems: ["installchecksheets", "installchecksheet", "eng2installchecklist"],
  vehicleloaded: ["vehicleinspection", "hqvehiclecheck"],
  stockcheck: ["partsinout"],
  loadlist: ["partsinout"],
  nearmiss: ["nearmissincident"],
  servicecheck: ["servicechecksheet"],
  vehiclecheck: ["hqvehiclecheck", "vehiclecheckdvsa", "vehicleinspection"],
};
const formNameMatches = (fn, label) => {
  if (reqMatch(fn, label)) return true;
  // Alias (interpretation-based) matching is HELD pending sign-off (UAT option B).
  // Only high-confidence exact/substring matches open a form; alias labels stay simple ticks.
  if (!ALIAS_MATCHING_ENABLED) return false;
  const a = _norm(fn), b = _norm(label);
  if (!a || !b) return false;
  for (const [k, vals] of Object.entries(REQ_FORM_ALIASES)) {
    if (b.includes(k) && vals.some((v) => a.includes(v))) return true;
  }
  return false;
};
const resolveReqForm = (forms, label) => (forms || []).find((x) => formNameMatches(x.name, label));
const fmtFull = (iso, start, end) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return `${ordinal(d.getDate())} ${d.toLocaleString("en-GB", { month: "short" })} ${d.getFullYear()} ${start || ""} - ${end || ""}`;
};

const TABS = ["Requirements", "Information", "Stops and Near Misses", "Load List", "Stats"];
const ACTIONS = [
  { key: "manage_reqs", label: "Manage Requirements", icon: ListChecks },
  { key: "public_dates", label: "Update Public Dates" },
  { key: "edit_status", label: "Edit Status" },
  { key: "login", label: "Add Login Status" },
  { key: "earning", label: "Add Earning" },
  { key: "form", label: "Add Form" },
  { key: "incident", label: "Add Incident" },
  { key: "survey", label: "Add Survey" },
];

function Dots({ n, tone }) {
  const c = { red: "bg-rose-500", amber: "bg-amber-500", green: "bg-emerald-500" }[tone] || "bg-slate-300";
  return <div className="flex gap-1">{[0, 1, 2, 3, 4].map((i) => <span key={i} className={`h-2.5 w-2.5 rounded-full ${i < n ? c : "bg-slate-200"}`} />)}</div>;
}

function LoadList({ items, vanName, onToggle }) {
  const loaded = items.filter((i) => i.loaded).length;
  if (items.length === 0) {
    return <div className="text-sm text-slate-400 py-6 text-center" data-testid="load-list-empty">No approved parts to load for this job yet.</div>;
  }
  return (
    <div data-testid="load-list">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-slate-600 flex items-center gap-1.5"><Truck className="w-4 h-4 text-indigo-500" />{vanName || "Van"}</div>
        <span className="text-xs font-semibold text-slate-500" data-testid="load-list-progress">{loaded} of {items.length} loaded</span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden mb-4"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${(loaded / items.length) * 100}%` }} /></div>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.request_id} className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${it.loaded ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200"}`} data-testid={`load-item-${it.request_id}`}>
            <button onClick={() => onToggle(it.request_id, !it.loaded)} aria-label={it.loaded ? "Mark not on van" : "Mark on van"}
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-colors ${it.loaded ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 text-transparent hover:border-indigo-400"}`}
              data-testid={`load-toggle-${it.request_id}`}>
              <Check className="w-4 h-4" />
            </button>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800 truncate">{it.product}</div>
              <div className="text-[11px] text-slate-400">Qty {it.qty_required}{it.loaded && it.loaded_by ? ` · loaded by ${it.loaded_by}` : ""}</div>
            </div>
            <span className={`text-[11px] font-semibold shrink-0 ${it.loaded ? "text-emerald-600" : "text-slate-400"}`}>{it.loaded ? "On van" : "Not loaded"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReqDetailPanel({ item, onClose }) {
  return (
    <SlidePanel onClose={onClose} maxW="max-w-md" testId="req-detail-panel">
      <div className="flex items-center justify-between border-b border-slate-100 pl-5 pr-14 py-4">
        <h3 className="text-lg font-bold text-slate-900">{item.label}</h3>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          {item.done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300" />}
          <span className={`text-sm font-semibold ${item.done ? "text-emerald-600" : "text-slate-500"}`} data-testid="req-detail-status">{item.done ? "Completed" : "Pending"}</span>
        </div>
        {item.section && <div className="text-xs uppercase tracking-wide text-slate-400">{item.section}</div>}
        {item.done ? (
          <div className="text-sm text-slate-600">Completed by {item.completed_by || "—"}{item.completed_at ? ` at ${item.completed_at}` : ""}</div>
        ) : (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-500">
            This check hasn’t been completed yet. It will be captured on-site via the engineer’s app, or a manager can mark it complete from <span className="font-medium text-slate-600">Manage Requirements</span>.
          </div>
        )}
      </div>
    </SlidePanel>
  );
}

export default function ShiftSummaryPanel({ shiftId, onClose, onEdit, onOpenDay, onReload }) {
  const [activeId, setActiveId] = useState(shiftId);
  useEffect(() => { setActiveId(shiftId); }, [shiftId]);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Requirements");
  const [menu, setMenu] = useState(false);
  const [manageReqs, setManageReqs] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  const [subs, setSubs] = useState([]);
  const [forms, setForms] = useState([]);
  const [picker, setPicker] = useState(false);
  const [fillId, setFillId] = useState(null);
  const [viewSub, setViewSub] = useState(null);
  const [reqDetail, setReqDetail] = useState(null);
  const [previewForm, setPreviewForm] = useState(null);
  const load = useCallback(() => { setData(null); api.get(`/scheduler/shifts/${activeId}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load shift")); }, [activeId]);
  const loadSubs = useCallback(() => { api.get("/submissions", { params: { shift: activeId } }).then((r) => setSubs(r.data.items || [])).catch(() => {}); }, [activeId]);
  const openSub = async (id) => { try { const r = await api.get(`/submissions/${id}`); setViewSub(r.data); } catch { toast.error("Could not open submission"); } };
  useEffect(() => { load(); loadSubs(); api.get("/forms").then((r) => setForms(r.data.forms || [])).catch(() => {}); }, [load, loadSubs]);

  const s = data?.shift;
  const canWrite = data?.can_write;

  const del = async () => {
    if (!window.confirm(`Delete shift ${s.display_no || s.shift_id}?`)) return;
    try { await api.delete(`/scheduler/shifts/${s.shift_id}`); toast.success("Shift deleted"); onReload?.(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const setStatus = async (st) => {
    try { await api.put(`/scheduler/shifts/${s.shift_id}`, { shift_status: st }); toast.success("Status updated"); setStatusOpen(false); load(); onReload?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const toggleReq = async (section, label, done) => {
    try { const r = await api.post(`/scheduler/shifts/${s.shift_id}/requirements`, { section, label, done }); setData((d) => ({ ...d, requirements: r.data.requirements })); onReload?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update requirement"); }
  };
  const toggleLoaded = async (rid, loaded) => {
    setData((d) => (d ? { ...d, load_list: (d.load_list || []).map((i) => (i.request_id === rid ? { ...i, loaded } : i)) } : d));
    try { const r = await api.post(`/scheduler/shifts/${activeId}/load/${rid}`, { loaded }); setData((d) => (d ? { ...d, load_list: r.data.load_list } : d)); }
    catch (e) {
      toast.error(e.response?.data?.detail || "Could not update load list");
      setData((d) => (d ? { ...d, load_list: (d.load_list || []).map((i) => (i.request_id === rid ? { ...i, loaded: !loaded } : i)) } : d));
    }
  };
  const openReqItem = (it, sectionTitle) => {
    const f = resolveReqForm(forms, it.label);
    if (f) { setPreviewForm(f.id); return; }
    setReqDetail({ ...it, section: sectionTitle });
  };
  const onAction = (key) => {
    setMenu(false);
    if (key === "manage_reqs") { setManageReqs(true); setTab("Requirements"); }
    else if (key === "edit_status") setStatusOpen(true);
    else toast.info("Demonstration action — not wired in this POC");
  };

  const sMeta = s ? statusMeta(s.shift_status) : null;

  return (
    <SlidePanel onClose={onClose} maxW="max-w-none" testId="shift-summary-panel">
      {!data ? <div className="flex-1 flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
        <>
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 pl-5 pr-14 py-4">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="text-xl font-bold text-slate-900 truncate">Shift {s.display_no || s.shift_id} - Summary</h3>
              {s.task_legacy_id && <a href={`/tasks/${s.task_legacy_id}`} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 hover:bg-indigo-100 hover:text-indigo-700 transition-colors" data-testid="shift-task-link">Task {s.task_legacy_id}</a>}
              {sMeta && <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${colourClasses(sMeta.colour).bg} ${colourClasses(sMeta.colour).border} ${colourClasses(sMeta.colour).text}`}><span className={`h-1.5 w-1.5 rounded-full ${colourClasses(sMeta.colour).dot}`} />{s.work_type}</span>}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button variant="outline" size="sm" onClick={() => window.open(`/scheduler/shifts/${activeId}/print`, "_blank")} data-testid="shift-print-btn"><Printer className="w-3.5 h-3.5 mr-1" />Print / Export</Button>
              {canWrite && (
                <>
                  <div className="relative">
                    <button onClick={() => setMenu((m) => !m)} className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50" data-testid="shift-actions-menu"><Menu className="w-4 h-4" /></button>
                    {menu && (
                      <div className="absolute right-0 top-10 z-40 w-56 rounded-xl border border-slate-200 bg-white py-1 shadow-xl" data-testid="shift-actions-list">
                        {ACTIONS.map((a) => <button key={a.key} onClick={() => onAction(a.key)} className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" data-testid={`action-${a.key}`}>{a.icon ? <a.icon className="w-4 h-4 text-slate-400" /> : <span className="w-4" />}{a.label}</button>)}
                      </div>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => onEdit(s)} data-testid="shift-edit-btn"><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                  <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={del} data-testid="shift-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-1 flex-col xl:flex-row overflow-hidden">
            <div className="xl:w-1/2 min-w-0 min-h-[280px] border-b xl:border-b-0 xl:border-r border-slate-100 bg-slate-50/60 flex flex-col">
              <RouteMapMini fill taskId={s.task_legacy_id} />
            </div>
            <div className="xl:w-1/2 min-w-0 flex flex-col sm:flex-row overflow-hidden">
              <div className="w-full sm:w-56 shrink-0 overflow-y-auto border-b sm:border-b-0 sm:border-r border-slate-100 bg-slate-50/60">
                <DayTimeline embedded steps={data.day_timeline} vehicleTitle={s.vehicle_title} activeShiftId={s.shift_id} onOpenShift={(id) => { if (id && id !== activeId) { setActiveId(id); setTab("Requirements"); setManageReqs(false); } }} onOpenDay={() => onOpenDay(s.vehicle_legacy_id, s.date)} />
              </div>
              <div className="w-full flex-1 min-w-0 overflow-y-auto p-5">
              {picker && <FormPicker forms={forms} onPick={(id) => { setPicker(false); setFillId(id); }} onClose={() => setPicker(false)} />}
              <div className="flex justify-end mb-2">
                {s.task_legacy_id && <a href={`/tasks/${s.task_legacy_id}`} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50" data-testid="view-shift-job"><ExternalLink className="w-3.5 h-3.5" />View Task</a>}
              </div>
              <dl className="space-y-2.5 text-sm">
                <Row label="Task ID" value={s.task_legacy_id ? <Link to={`/tasks/${s.task_legacy_id}`} className="text-indigo-600 hover:text-indigo-700 hover:underline" data-testid="shift-detail-task-link">{s.task_legacy_id}</Link> : "—"} />
                <Row label="Shift ID" value={s.display_no || s.shift_id} />
                <Row label="Time" value={<span><Clock className="inline w-4 h-4 text-slate-400 mr-1" />{fmtFull(s.date, s.start_time, s.end_time)} {s.logged_by && <span className="text-slate-400">(Logged By {s.logged_by})</span>}</span>} />
                <Row label="Location" value={<span><MapPin className="inline w-4 h-4 text-slate-400 mr-1" />{s.location || "—"}</span>} />
                <Row label="Resources" value={<div className="flex flex-wrap gap-1.5">{data.resources.map((r, i) => <ResourceChip key={i} name={r.name} kind={r.kind} job_title={r.job_title} to={r.to} />)}</div>} />
                {s.weather && <Row label="Weather" value={<span className="text-slate-600"><Cloud className="inline w-4 h-4 text-slate-400 mr-1" />{s.weather.label} <Thermometer className="inline w-4 h-4 text-slate-400 ml-2 mr-0.5" />{s.weather.temp}</span>} />}
              </dl>

              <ShiftEta taskId={s.task_legacy_id} />
              {data.crew_activity && <CrewActivity data={data.crew_activity} />}

              {statusOpen && (
                <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3" data-testid="edit-status-box">
                  <div className="text-xs font-semibold text-slate-500 mb-1.5">Set shift status</div>
                  <div className="flex flex-wrap gap-1.5">
                    {SHIFT_STATUS_ORDER.map((k) => { const m = SHIFT_STATUS_META[k], c = colourClasses(m.colour); return <button key={k} onClick={() => setStatus(k)} className={`rounded-full border px-2.5 py-1 text-xs ${s.shift_status === k ? `${c.bg} ${c.border} ${c.text} font-semibold` : "border-slate-200 text-slate-500 hover:bg-slate-50"}`} data-testid={`set-status-${k}`}>{m.label}</button>; })}
                  </div>
                </div>
              )}

              <div className="mt-5 flex gap-5 overflow-x-auto border-b border-slate-200 text-sm">
                {TABS.map((t) => <button key={t} onClick={() => setTab(t)} className={`-mb-px shrink-0 whitespace-nowrap border-b-2 pb-2 ${tab === t ? "border-indigo-500 text-indigo-600 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid={`tab-${t.replace(/\s+/g, "-").toLowerCase()}`}>{t}</button>)}
              </div>

              <div className="pt-4">
                {tab === "Requirements" && <Requirements req={data.requirements} manage={manageReqs && canWrite} onToggle={toggleReq} submissions={subs} onAddForm={() => setPicker(true)} onViewSub={openSub} onOpenItem={openReqItem} />}
                {tab === "Information" && <Information info={data.info} />}
                {tab === "Stops and Near Misses" && <div className="text-sm text-slate-400 py-6 text-center" data-testid="tab-stops-empty">{(data.stops_near_misses || []).length === 0 ? "No stops or near misses recorded." : ""}</div>}
                {tab === "Load List" && <LoadList items={data.load_list || []} vanName={s.vehicle_title} onToggle={toggleLoaded} />}
                {tab === "Stats" && <Stats stats={data.stats} />}
              </div>
            </div>
            </div>
          </div>
          {fillId && <FormFiller formId={fillId} context={{ shift_id: s.shift_id, task_legacy_id: s.task_legacy_id }} onClose={() => setFillId(null)} onSubmitted={() => { setFillId(null); loadSubs(); load(); }} />}
          {viewSub && <FormFiller submission={viewSub} context={{ shift_id: s.shift_id, task_legacy_id: s.task_legacy_id }} mapTaskId={s.task_legacy_id} onClose={() => setViewSub(null)} />}
          {previewForm && <FormFiller formId={previewForm} preview canFill={canWrite} context={{ shift_id: s.shift_id, task_legacy_id: s.task_legacy_id }} onClose={() => setPreviewForm(null)} onSubmitted={() => { setPreviewForm(null); loadSubs(); load(); }} />}
          {reqDetail && <ReqDetailPanel item={reqDetail} onClose={() => setReqDetail(null)} />}
        </>
      )}
    </SlidePanel>
  );
}

function FormPicker({ forms, onPick, onClose }) {
  return (
    <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-3" data-testid="shift-form-picker">
      <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-slate-700">Choose a form to fill</span><button onClick={onClose} className="text-xs text-slate-400 hover:text-slate-700">Close</button></div>
      <div className="max-h-52 overflow-y-auto space-y-1">
        {forms.length === 0 ? <div className="text-xs text-slate-400">No forms available.</div> :
          forms.map((f) => <button key={f.id} onClick={() => onPick(f.id)} className="block w-full text-left rounded-lg bg-white border border-slate-200 px-3 py-1.5 text-sm hover:border-indigo-300" data-testid={`shift-pick-form-${f.id}`}>{f.name}<span className="block text-[11px] text-slate-400">{f.category || "Uncategorised"}</span></button>)}
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="grid grid-cols-[110px_1fr] gap-3 items-start">
    <dt className="text-slate-400">{label}</dt><dd className="text-slate-800">{value}</dd>
  </div>
);

function ReqSection({ title, section }) {
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold text-slate-700">{title}</h5>
        <span className={`rounded-full px-2 py-0.5 text-xs ${section.complete ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{section.status}</span>
      </div>
    </div>
  );
}

function Requirements({ req, manage, onToggle, submissions, onAddForm, onViewSub, onOpenItem }) {
  const render = (key, title) => {
    const sec = req?.[key]; if (!sec) return null;
    return (
      <div className="mb-5" data-testid={`req-${key}`}>
        <ReqSection title={title} section={sec} />
        <ul className="space-y-2">
          {sec.items.map((it, idx) => {
            const match = (submissions || []).find((su) => formNameMatches(su.form_name, it.label));
            return (
            <li key={it.form_id || `${it.label}-${idx}`} className="flex items-center justify-between gap-2">
              <button onClick={() => { if (manage) onToggle(key, it.label, !it.done); else if (match) onViewSub(match.id); else onOpenItem(it, title); }} className="flex items-center gap-2 text-sm text-left group" data-testid={`req-item-${it.label.replace(/\s+/g, "-").toLowerCase()}`}>
                {it.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-slate-300" />}
                <span className="text-slate-700 group-hover:text-indigo-600">{it.label}</span>
                <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-indigo-500" />
              </button>
              <span className="text-xs text-slate-400">{it.done ? `Completed by ${it.completed_by} at ${it.completed_at}` : (manage ? "Tap to complete" : "Pending")}</span>
            </li>
          ); })}
        </ul>
      </div>
    );
  };
  return (
    <div>
      {manage && <div className="mb-3 rounded-lg bg-indigo-50/60 px-3 py-2 text-xs text-indigo-700" data-testid="manage-reqs-banner">Manage mode — tap an item to toggle completion.</div>}
      {render("pre_site", "Pre Site")}
      {render("on_site", "On Site")}
      {render("post_site", "Post Site Checks")}
      <div className="mt-2 border-t border-slate-100 pt-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-600">Additional Forms</span>
          <button className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-0.5 text-xs hover:bg-slate-50" onClick={onAddForm} data-testid="req-add-form"><Plus className="w-3 h-3" /> Add</button>
        </div>
        {(submissions || []).length === 0 ? <div className="text-xs text-slate-400">None</div> : (
          <ul className="space-y-1">
            {submissions.map((sub) => (
              <li key={sub.id}><button onClick={() => onViewSub(sub.id)} className="w-full text-left rounded-lg border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50" data-testid={`shift-sub-${sub.id}`}><span className="font-medium text-slate-700">{sub.form_name}</span><span className="text-xs text-slate-400"> · {sub.submitted_by}</span></button></li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 py-3">
      <span className="text-sm text-slate-600">{label}</span>
      <span className="flex items-center gap-2 text-xs text-slate-400">{value}<button className="rounded-md border border-slate-200 px-2 py-0.5 hover:bg-slate-50" onClick={() => toast.info("Demonstration action")}><Plus className="inline w-3 h-3" /> Add</button></span>
    </div>
  );
}
function Information({ info }) {
  return (
    <div data-testid="info-panel">
      <InfoRow label="Earnings" value={info.earnings ? `£${info.earnings}` : "None"} />
      <InfoRow label="Login Statuses" value={<span className="rounded-full bg-purple-100 px-2 py-0.5 text-purple-700">{info.login_statuses || 0}</span>} />
      <InfoRow label="Labour and Travel" value={info.labour_travel || "None"} />
      <InfoRow label="Finance Breakdown" value={info.finance_breakdown || ""} />
    </div>
  );
}

function Stats({ stats }) {
  return (
    <div className="space-y-4" data-testid="stats-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div><div className="text-xs text-slate-500">Expected Average Speed</div><div className="text-lg font-bold text-emerald-600">{stats.expected_average_speed}</div></div>
          <div><div className="text-xs text-slate-500">Actual Average Speed</div><div className="text-lg font-bold text-rose-500">{stats.actual_average_speed}</div></div>
        </div>
        <div className="space-y-2.5">
          {[["Average Speed", stats.ratings.average_speed, "red"], ["Efficiency", stats.ratings.efficiency, "amber"], ["Profit", stats.ratings.profit, "red"], ["Form Completion", stats.ratings.form_completion, "green"]].map(([l, n, t]) => (
            <div key={l} className="flex items-center justify-between"><span className="text-sm text-slate-600">{l}</span><Dots n={n} tone={t} /></div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-4">
        <div><div className="text-xs text-slate-500">Unauthorised Stops</div><div className="text-lg font-bold text-slate-800">{stats.unauthorised_stops}</div><div className="text-xs text-slate-400">{stats.unauthorised_note}</div></div>
        <div><div className="text-xs text-slate-500">Distance Travelled</div><div className="text-lg font-bold text-amber-600">{stats.distance_travelled}</div>{stats.distance_estimated && <div className="text-xs text-slate-400">{stats.distance_estimated}</div>}</div>
      </div>
    </div>
  );
}

function EtaStat({ label, value, tone }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 px-2 py-1.5 text-center">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-sm font-bold ${tone || "text-slate-800"}`}>{value}</div>
    </div>
  );
}

function CrewActivity({ data }) {
  const tone = (a) => (a === "on_site" || a === "finished" ? "text-emerald-600" : a === "en_route" ? "text-amber-600" : "text-slate-400");
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="crew-activity-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-indigo-500" /> Crew activity</span>
        <span className="text-[11px] rounded-full bg-slate-100 px-2 py-0.5 text-slate-600" data-testid="crew-job-status">Job: {(data.job_status || "").replace(/_/g, " ")}</span>
      </div>
      {data.exception && <div className="mb-2 rounded-lg bg-rose-50 border border-rose-200 px-2.5 py-1.5 text-[11px] text-rose-700" data-testid="crew-exception-note">A crew member has not arrived while the job is in progress.</div>}
      <ul className="space-y-1.5">
        {data.members.map((m) => (
          <li key={m.id} className="flex items-center justify-between text-sm" data-testid={`crew-member-${m.id}`}>
            <span className="flex items-center gap-1.5 text-slate-700">{m.name}{m.is_lead && <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 text-[9px] font-bold text-emerald-800"><Star className="w-2 h-2" />Lead</span>}</span>
            <span className="flex items-center gap-2"><span className={`text-xs font-semibold ${tone(m.activity)}`}>{m.activity_label}</span>{m.at && <span className="text-[10px] text-slate-400">{new Date(m.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ShiftEta({ taskId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    if (!taskId) return;
    setLoading(true);
    api.get(`/tasks/${taskId}/shift-eta`).then((r) => setData(r.data)).catch(() => setData({ available: false, reason: "Could not load ETA." })).finally(() => setLoading(false));
  }, [taskId]);
  useEffect(() => { load(); }, [load]);
  const t = data?.traffic;
  const fmtT = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—");
  if (!taskId) return null;
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="shift-summary-eta">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Navigation className="w-3.5 h-3.5 text-indigo-500" /> Live drive to site</span>
        <button onClick={load} disabled={loading} className="text-xs font-medium text-indigo-600 hover:underline flex items-center gap-1" data-testid="shift-eta-refresh">{loading && <Loader2 className="w-3 h-3 animate-spin" />}Refresh</button>
      </div>
      {!data ? <div className="text-xs text-slate-400">Loading…</div>
        : !data.available ? <div className="text-xs text-slate-400">{data.reason}</div>
        : (
          <>
            <div className="text-xs text-slate-600"><span className="font-medium">{data.from.label}</span> <span className="text-slate-400">→</span> <span className="font-medium">{data.to.label}</span></div>
            {data.origin_kind === "depot" && <div className="text-[11px] text-amber-600 mt-0.5" data-testid="shift-eta-origin-note">No earlier shift found — measured from the HQ depot.</div>}
            {t?.enabled ? (
              <div className="mt-2" data-testid="shift-eta-live">
                <div className="grid grid-cols-3 gap-2">
                  <EtaStat label="Distance" value={`${t.miles} mi`} />
                  <EtaStat label="With traffic" value={fmtDur(t.duration_traffic_mins)} tone="text-rose-600" />
                  <EtaStat label="Typical" value={t.duration_typical_mins != null ? fmtDur(t.duration_typical_mins) : "—"} />
                </div>
                <div className="flex items-center justify-between pt-2">
                  <span className="text-xs text-slate-500">Arrival ETA</span><span className="text-sm font-bold text-slate-900">{fmtT(t.eta_iso)}</span>
                </div>
                {t.delay_mins != null && t.delay_mins > 0 && <div className="text-[11px] text-amber-600 mt-0.5">+{fmtDur(t.delay_mins)} vs typical due to traffic</div>}
              </div>
            ) : <div className="mt-2 text-[11px] text-amber-700" data-testid="shift-eta-disabled">Live traffic ETA not enabled. {t?.reason}</div>}
          </>
        )}
    </div>
  );
}
